import { api } from '@/global/services/api'
import { dateService, FORMATS } from '@/global/services/date'
import documentsHistoryHelper from '../documents-history-helper'
import { notify } from '@/global/services/notification/notificationBus'
import tabsHelper from '../tabs-helper'
import { getBooleanFromValue, isObject } from '@/global/utils/utils'

const DOCUMENTS_HISTORY_DATA_KEY = 'prpPermitDocuments'
const DOCUMENT_TYPE = 'traffic_regulation_permit'
export default {

  mixins: [
    documentsHistoryHelper,
    tabsHelper
  ],

  data () {
    return {
      prpPermitHistoryVisible: false,
      prpPermitShowEditButtons: false,
      prpPermitEditState: false,
      prpPermitShowDocumentPreview: false,
      prpPermitDocument: null,
      prpPermitDataForGenerationVisible: true,
      prpPermitTitle: this.$t('road-maintenance/aggregator.prp_permit'),
      prpPermitBaseTitle: this.$t('road-maintenance/aggregator.prp_permit'),
      prpPermitValidationErrors: {},
      prpPermitDocumentHistoryLoaded: false,
      prpPermitRefreshDisabled: false,
      prpPermitSendForProcessingBtnVisible: false,
      permitDocumentLoaders: [],
      showPermitTabLoader: null
    }
  },

  computed: {
    prpPermitConfig () {
      return {
        title: this.$t('road-maintenance/aggregator.prp_permit'),
        visible: this.$route.query.category !== 'request_new',
        key: 'prpPermit',
        validationErrors: this.prpPermitValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.prpPermitEditState,
        editUpdate: (event) => this.onPrpPermitEditUpdate(event),
        dataFetched: (event) => this.onPrpPermitDataFetched(event, false, true),
        afterCancel: (event) => this.onPrpPermitDataFetched(event),
        afterSave: (event) => this.onPrpPermitDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onPrpPermitModifyData(event),
        showLoader: this.showPermitTabLoader,
        hiddenFormKeys: [
          'html',
          'documents',
          'files',
          'damages',
          'work_calculation'
        ],
        tabContent: [
          // title with buttons
          {
            component: this.tabActionsComponent,
            title: this.prpPermitTitle,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            showEditButtons: this.prpPermitShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.prpPermitShowEditButtons,
                disabled: this.prpPermitRefreshDisabled,
                click: async (event) => {
                  const { fetchTabData } = event || {}
                  this.prpPermitRefreshDisabled = true

                  await fetchTabData?.()
                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  setTimeout(() => {
                    this.prpPermitRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.prpPermitShowEditButtons && !!this.permissions?.canChangeStatus,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'base',
                label: this.$t('road-maintenance/temporary_regulation.send_request_for_processing'),
                prependIcon: 'mdi-send-variant',
                prependIconStyle: {
                  fontSize: '20px'
                },
                visible: !this.prpPermitShowEditButtons && !!this.permissions?.canChangeStatus && this.prpPermitSendForProcessingBtnVisible,
                click: () => this.sendRequestForProcessing()
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.prpPermitShowEditButtons && !!this.permissions?.canCreate,
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuItemClick: (event) => this.prpPermitMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.viewPdfBtnLabel,
                color: '#6e736d',
                visible: this.prpPermitShowEditButtons,
                class: 'generate-document-btn',
                loaderConfig: {
                  opacity: 0,
                  color: '#FFFFFFF'
                },
                showLoader: this.showPreviewLoader,
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.prpPermitBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onPrpPermitCancelTab(),
            onSaveClick: (event) => this.onPrpPermitSave(event)
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_prp_permit_generation'),
            visible: this.prpPermitDataForGenerationVisible,
            rows: [
              // meta data
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'doc_class',
                    label: this.$t('road-maintenance/aggregator.class'),
                    row: 1,
                    column: 1,
                    colSpan: 1,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'registry_number',
                    row: 1,
                    column: 2,
                    colSpan: 1,
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.registration_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'created_at',
                    type: 'datetime-picker',
                    subType: 'date',
                    row: 1,
                    column: 3,
                    colSpan: 1,
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    label: this.$t('road-maintenance/aggregator.document_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          created_at: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'addresses',
                    label: this.$t('road-maintenance/aggregator.handover_road'),
                    row: 2,
                    column: 1,
                    colSpan: 1,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'traffic_regulation_investor_name',
                    label: this.$t('road-maintenance/aggregator.traffic_regulation_investor'),
                    row: 2,
                    column: 2,
                    colSpan: 1,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'completion_date',
                    row: 2,
                    column: 3,
                    colSpan: 1,
                    label: this.$t('road-maintenance/prp.work_finished_at_date'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'work_type',
                    type: 'textarea',
                    label: this.$t('road-maintenance/aggregator.type_of_works'),
                    row: 3,
                    column: 1,
                    colSpan: 3,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.permit_valid_from_to'),
                    row: 4,
                    column: 1,
                    colSpan: 1.5,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.requested_date_from_request'),
                    row: 4,
                    column: 2,
                    colSpan: 1.5,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    key: 'valid_from',
                    type: 'datetime-picker',
                    row: 5,
                    column: 1,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.datetimeShort,
                    placeholder: FORMATS.datetimeShort,
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          valid_from: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'valid_to',
                    type: 'datetime-picker',
                    row: 5,
                    column: 1,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.datetimeShort,
                    placeholder: FORMATS.datetimeShort,
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          valid_to: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'date_time_from',
                    row: 5,
                    column: 2,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar-month',
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateData, formData } = event || {}
                      if (value && typeof updateData === 'function' && !formData.valid_from) {
                        updateData({
                          valid_from: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'date_time_to',
                    row: 5,
                    column: 2,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar-month',
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateData, formData } = event || {}
                      if (value && typeof updateData === 'function' && !formData.valid_to) {
                        updateData({
                          valid_to: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'permit_delivery',
                    type: 'select',
                    row: 6,
                    column: 1,
                    colSpan: 3,
                    multiple: true,
                    items: this.dropdownValues?.traffic_regulation_permit_delivery || [],
                    customItemText: (item) => {
                      return item?.description ? this.$t(item.description) : null
                    },
                    upperLabel: this.$t('road-maintenance/aggregator/prp_approval.deliver'),
                    creatable: false,
                    editable: false,
                    unlock: true,
                    visible: true
                  }
                ]
              }
            ]
          },
          // documents history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.prpPermitHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance.date') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'status',
                title: this.$t('road-maintenance/excavation-service.status') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance.user') + ':'
              },
              {
                key: 'document',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                visible: (event) => {
                  return event?.blockValue !== '-'
                },
                loaderIds: this.permitDocumentLoaders,
                valueClass: 'history-block-value',
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name ? data.document.name : '-'
                },
                click: (event) => this.onPermitDownloadDocument(event)
              },
              {
                visible: (event) => {
                  return !!event?.rowData?.document_data_id
                },
                component: this.createFromVersionBtnComponent,
                class: 'create-from-version-btn-block',
                onClick: (event) => this.onPermitCreateFromVersion(event)
              }
            ]
          },
          // handover documents preview
          {
            component: this.documentPreviewComponent,
            visible: this.prpPermitShowDocumentPreview,
            content: this.prpPermitDocument
          }
        ]
      }
    }
  },

  methods: {
    async onPermitDownloadDocument (event) {
      const { id: documentId } = event?.rowData || {}
      // skip if download is already in progress
      if (this.permitDocumentLoaders.includes(documentId)) {
        return
      }
      // show document loader
      if (documentId && !this.permitDocumentLoaders.includes(documentId)) {
        this.permitDocumentLoaders.push(documentId)
      }

      // download document
      await this.downloadDocument(event)
      // stop document loader
      if (this.permitDocumentLoaders.includes(documentId)) {
        this.permitDocumentLoaders = this.permitDocumentLoaders.filter(activeLoaderId => activeLoaderId !== documentId)
      }
    },

    async onPrpPermitGenerateDocument (event, showLast = false) {
      try {
        const { formData } = event || {}
        const { id } = this?.$route?.params || {}
        this.showPermitTabLoader = true

        // validate data
        if (!id) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Missing traffic regulation id')
          return
        }
        if (!isObject(formData)) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Form data is invalid', formData)
          return
        }

        // Create formData object for sending
        const dataToSend = new FormData()
        // format dates
        dataToSend.append('valid_from', formData?.valid_from ? dateService.formatCustom(formData.valid_from, FORMATS.datetimeShort, FORMATS.isoDatetime) : '')
        dataToSend.append('valid_to', formData?.valid_to ? dateService.formatCustom(formData.valid_to, FORMATS.datetimeShort, FORMATS.isoDatetime) : '')
        dataToSend.append('created_at', formData?.created_at ? dateService.formatCustom(formData.created_at, FORMATS.date, FORMATS.isoDate) : '')
        // add permit delivery ids
        if (formData?.permit_delivery?.length) {
          formData.permit_delivery.forEach(permitDeliveryId => {
            dataToSend.append('permit_delivery[]', permitDeliveryId)
          })
        }
        // show last document version flag
        dataToSend.append('show_last_version', showLast)

        // Get document html
        const { data } = await api('road-maintenance').post(`traffic-regulation/${id}/${DOCUMENT_TYPE}`, dataToSend)

        this.prpPermitDocument = data
        this.$set(this.chooseSolutionTypeDialogConfig, 'showDialog', false)
        this.prpPermitShowDocumentPreview = true
        this.prpPermitEditState = true
        this.prpPermitShowEditButtons = true
        this.prpPermitDataForGenerationVisible = false
        this.prpPermitTitle = this.$t('road-maintenance/aggregator.prp_permit_overview')
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.prpPermitValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
      finally {
        this.showPermitTabLoader = false
      }
    },

    async onPermitCreateFromVersion (event) {
      try {
        this.showPermitTabLoader = true

        // get document data
        const data = await this.createFromVersion(event, DOCUMENT_TYPE)

        const { html, css } = data || {}
        // do not open document if data is invalid
        if (!html && !css) {
          return
        }

        // set document data and open document preview
        this.prpPermitDocument = data
        this.$set(this.chooseSolutionTypeDialogConfig, 'showDialog', false)
        this.prpPermitShowDocumentPreview = true
        this.prpPermitEditState = true
        this.prpPermitShowEditButtons = true
        this.prpPermitDataForGenerationVisible = false
        this.prpPermitTitle = this.$t('road-maintenance/aggregator.prp_permit_overview')
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showPermitTabLoader = false
      }
    },

    onPrpPermitCancelTab () {
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.prpPermitCancel())
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    prpPermitCancel () {
      this.prpPermitEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
    },

    onPrpPermitSave (event) {
      const { saveTab } = event || {}
      this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
      this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
      this.$set(this.informationDialogConfig, 'cancelButtonText', this.$t('base.cancel'))
      this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.document_will_be_uploaded_to_centrix_message'))
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onPrpPermitSaveConfirmation(saveTab))
      this.$set(this.informationDialogConfig, 'showDialog', true)
    },

    onPrpPermitSaveConfirmation (saveTabFn) {
      if (typeof saveTabFn === 'function') {
        saveTabFn()
      }

      this.prpPermitEditState = false
      this.$set(this.informationDialogConfig, 'showDialog', false)
      this.$set(this.informationDialogConfig, 'title', this.informationDialogBaseConfig.title)
      this.$set(this.informationDialogConfig, 'confirmCallbackFunction', this.informationDialogBaseConfig.confirmCallbackFunction)
    },

    onPrpPermitEditUpdate (event) {
      const { edit } = event || {}
      this.prpPermitHistoryVisible = !edit

      if (!edit) {
        this.prpPermitShowDocumentPreview = false
        this.prpPermitEditState = false
        this.prpPermitShowEditButtons = false
        this.prpPermitDataForGenerationVisible = true
        this.prpPermitDocument = null
        this.prpPermitTitle = this.prpPermitBaseTitle
      }
    },

    async onPrpPermitDataFetched (event, afterSave = false, onFetch = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}

      const data = apiData?.data || formDataForSend || {}

      this.prpPermitSendForProcessingBtnVisible = getBooleanFromValue(data?.is_incoming_request_btn_visible)

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          valid_from: data?.valid_from ? dateService.formatCustom(data.valid_from, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          valid_to: data?.valid_to ? dateService.formatCustom(data.valid_to, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          created_at: data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDate, FORMATS.date) : null
        })
        if (onFetch) {
          updateData({
            permit_delivery: this.dropdownValues?.traffic_regulation_permit_delivery?.map(val => val?.id)
          })
        }
      }

      if (!this.prpPermitDocumentHistoryLoaded || afterSave) {
        this.prpPermitDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // Fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // Open change status popup after documents save only if we have suggested status provided
        if (suggestedStatusId) {
          this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
          const message = this.$t('road-maintenance/aggregator.status_change_message')
          this.$set(this.informationDialogConfig, 'showDialog', afterSave)
          this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
        }
      }
    },

    onPrpPermitModifyData (event) {
      const { copiedData } = event || {}
      if (copiedData) {
        copiedData.valid_from = copiedData?.valid_from ? dateService.formatCustom(copiedData.valid_from, FORMATS.datetimeShort, FORMATS.isoDatetime) : null
        copiedData.valid_to = copiedData?.valid_to ? dateService.formatCustom(copiedData.valid_to, FORMATS.datetimeShort, FORMATS.isoDatetime) : null
        copiedData.created_at = copiedData?.created_at ? dateService.formatCustom(copiedData.created_at, FORMATS.date, FORMATS.isoDate) : null
      }

      return copiedData
    },

    async prpPermitMenuItemClick (event) {
      const { menuIndex } = event || {}
      await this.onPrpPermitGenerateDocument(event, menuIndex === 1)
    },

    sendRequestForProcessing () {
      this.requestForProcessingDialogConfig.showDialog = true
    }
  }
}
